import { useState } from 'react'
import type { User } from '../../api/types'
import { Avatar } from '../../components/Avatar'
import { Icon } from '../../components/Icon'
import { useDebounced } from '../../hooks/useDebounced'
import { isBotUser } from '../../lib/bots'
import { BotProfileButton } from '../bots/BotName'
import { useDirectorySearch } from './directorySearch'
import { PersonText } from './PersonText'

interface Props {
  selected: User[]
  onChange: (users: User[]) => void
  /** Users that cannot be picked (already members, me). */
  excludeIds?: string[]
  single?: boolean
  label?: string
  /** Offer the listed bots the directory returns (new messages and group chats; channels add bots separately). */
  includeBots?: boolean
}

/** Staff directory search (GET /api/users?q=) with selected chips. */
export function UserPicker({ selected, onChange, excludeIds = [], single = false, label = 'Search staff', includeBots = false }: Props) {
  const [query, setQuery] = useState('')
  const debounced = useDebounced(query.trim(), 250)
  const { results, status, error } = useDirectorySearch(debounced, includeBots)

  const selectedIds = new Set(selected.map((u) => u.id))
  const visible = results.filter((u) => !excludeIds.includes(u.id))

  const toggle = (user: User) => {
    if (selectedIds.has(user.id)) onChange(selected.filter((u) => u.id !== user.id))
    else onChange(single ? [user] : [...selected, user])
  }

  return (
    <div>
      {selected.length ? (
        <div className="picker-selection" aria-label="Selected people">
          {selected.map((u) => (
            <span key={u.id} className="member-chip">
              {u.displayName}
              <button type="button" aria-label={`Remove ${u.displayName}`} onClick={() => toggle(u)}>
                <Icon name="x" />
              </button>
            </span>
          ))}
        </div>
      ) : null}
      <div className="search-field" style={{ marginBottom: 8 }}>
        <Icon name="search" />
        <input type="search" placeholder={label} aria-label={label} autoComplete="off" value={query} onChange={(e) => setQuery(e.target.value)} data-autofocus />
      </div>
      <div className="picker-results" aria-label="People">
        {status === 'loading' && visible.length === 0 ? <p className="picker-status">Searching…</p> : null}
        {status === 'error' ? <p className="picker-status">{error}</p> : null}
        {status === 'idle' && visible.length === 0 ? <p className="picker-status">No people found.</p> : null}
        {visible.map((user) => {
          const option = (
            <button key={user.id} type="button" className="picker-person" aria-pressed={selectedIds.has(user.id)} onClick={() => toggle(user)}>
              <Avatar user={user} />
              <PersonText user={user} />
              <span className="picker-check" aria-hidden="true">
                {selectedIds.has(user.id) ? <Icon name="check" /> : null}
              </span>
            </button>
          )
          if (!isBotUser(user)) return option
          return (
            <div key={user.id} className="picker-row">
              {option}
              <BotProfileButton userId={user.id} label={`About ${user.displayName}`} className="icon-button">
                <Icon name="info" />
              </BotProfileButton>
            </div>
          )
        })}
      </div>
    </div>
  )
}
